import { CloseOutlined, FilterOutlined } from "@ant-design/icons"
import { Button, Drawer, Flex, Grid, Popover } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../../components/Filters/DateRangeFilter.tsx"
import { PresetDateRangeFilter } from "../../../../components/Filters/PresetDateRangeFilter.tsx"
import { DebouncedInput } from "../../../../components/Input/DebouncedInput.tsx"
import { cn } from "../../../../utils/cn.ts"
import { CREATED_DATE_FILTER_FIELD } from "../../constants/created.ts"
import {
  isArchivingStatus,
  withoutArchivingStatuses
} from "../../constants/project-statuses.ts"
import {
  SCHEDULE_FILTER_STATES,
  scheduleFilterLabel
} from "../../constants/schedule-health.ts"
import type { ProjectScope } from "../../helpers/projects-url-state.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"
import { FilterMenuButton, type FilterMenuOption } from "./FilterMenuButton.tsx"
import { CLEARED_FILTERS, activeFilterChips } from "./active-filter-chips.ts"

interface ProjectCommandToolbarProps {
  filters: ProjectFilters
  filterOptions: Record<string, BaseOptionType[]>
  scope: ProjectScope
  onScopeChange: (scope: ProjectScope) => void
  onFiltersChange: (patch: Partial<ProjectFilters>) => void
}

const asList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(String)
    : typeof value === "string" && value
      ? value.split(",")
      : []

const toOptions = (options: BaseOptionType[] | undefined): FilterMenuOption[] =>
  (options ?? []).map((option) => ({
    label: String(option.label ?? option.value ?? ""),
    value: String(option.value ?? "")
  }))

// One height and one outline for every control on the row, from the theme's
// control border rather than the faint container border that read as disabled.
const CONTROL = "h-8 border-slate-300 text-slate-800"

export function ProjectCommandToolbar({
  filters,
  filterOptions,
  scope,
  onScopeChange,
  onFiltersChange
}: ProjectCommandToolbarProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  // Breakpoints are unknown on the first render; assume the wide layout then.
  const compact = screens.lg === false
  const [drawerOpen, setDrawerOpen] = useState(false)

  const setList = (key: keyof ProjectFilters) => (next: string[]) =>
    onFiltersChange({ [key]: next.length ? next : null })

  const statusOptions = useMemo(
    () =>
      toOptions(
        (filterOptions?.project_status ?? []).filter(
          (option) => !isArchivingStatus(option?.value)
        )
      ),
    [filterOptions]
  )
  const scheduleOptions = useMemo(
    () =>
      SCHEDULE_FILTER_STATES.map((state) => ({
        value: state,
        label: scheduleFilterLabel(t, state)
      })),
    [t]
  )

  const chips = activeFilterChips(filters, filterOptions, t)
  // One per control behind More filters, however many of its params are set.
  const moreActive =
    Number(!!(filters?.end_date_after || filters?.end_date_before)) +
    Number(
      !!(
        filters?.created_window ||
        filters?.created_after ||
        filters?.created_before
      )
    )

  const filterButtons = (
    <>
      <FilterMenuButton
        label={t("projectsCommand.filters.status")}
        options={statusOptions}
        value={asList(withoutArchivingStatuses(filters?.project_status))}
        onChange={setList("project_status")}
        className={CONTROL}
      />
      <FilterMenuButton
        label={t("projectsCommand.filters.projectManager")}
        options={toOptions(filterOptions?.project_manager)}
        value={asList(filters?.project_manager)}
        onChange={setList("project_manager")}
        searchable
        className={CONTROL}
      />
      <FilterMenuButton
        label={t("projectsCommand.filters.projectType")}
        options={toOptions(filterOptions?.project_type_ids)}
        value={asList(filters?.project_type)}
        onChange={setList("project_type")}
        className={CONTROL}
      />
      <FilterMenuButton
        label={t("projectsCommand.filters.schedule")}
        options={scheduleOptions}
        value={asList(filters?.schedule_health)}
        onChange={setList("schedule_health")}
        className={CONTROL}
      />
      <DebouncedInput
        allowClear
        value={filters?.customer_name}
        placeholder={t("projectsCommand.filters.customer")}
        aria-label={t("projectsCommand.filters.customer")}
        onValueChange={(customer_name) => onFiltersChange({ customer_name })}
        className={cn(
          CONTROL,
          "w-44 max-lg:w-full [&_input::placeholder]:!text-slate-600"
        )}
      />
    </>
  )

  const moreFilters = (
    <Flex vertical gap={12} className="w-72 max-lg:w-full">
      <DateRangeFilter<ProjectFilters>
        after={filters?.end_date_after}
        before={filters?.end_date_before}
        afterKey="end_date_after"
        beforeKey="end_date_before"
        placeholder={t("filters.dueDatePlaceholder")}
        onChange={onFiltersChange}
        className="w-full"
      />
      <PresetDateRangeFilter<ProjectFilters>
        presets={CREATED_DATE_FILTER_FIELD.presets}
        preset={filters?.created_window}
        after={filters?.created_after}
        before={filters?.created_before}
        presetKey={CREATED_DATE_FILTER_FIELD.presetKey}
        afterKey={CREATED_DATE_FILTER_FIELD.key}
        beforeKey={CREATED_DATE_FILTER_FIELD.endKey}
        label={CREATED_DATE_FILTER_FIELD.label}
        placeholder={CREATED_DATE_FILTER_FIELD.placeholder}
        allTimeLabel={CREATED_DATE_FILTER_FIELD.allTimeLabel}
        customLabel={CREATED_DATE_FILTER_FIELD.customLabel}
        className="w-full"
        onChange={onFiltersChange}
      />
    </Flex>
  )

  const scopeSwitch = (
    <div
      role="group"
      aria-label={t("projectsCommand.scope.label")}
      className="inline-flex h-8 rounded-md border border-solid border-slate-300 bg-slate-50 p-0.5"
    >
      {(["mine", "all"] as const).map((value) => {
        const selected = scope === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={selected}
            onClick={() => onScopeChange(value)}
            className={cn(
              "cursor-pointer rounded border border-solid px-3 text-sm leading-none transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal-700",
              selected
                ? "border-teal-600 bg-white font-medium text-teal-800 shadow-sm"
                : "border-transparent bg-transparent text-slate-700 hover:text-slate-900"
            )}
          >
            {t(`projectsCommand.scope.${value}`)}
          </button>
        )
      })}
    </div>
  )

  const clearAll = chips.length ? (
    <Button
      type="link"
      className="h-8 px-1"
      onClick={() => onFiltersChange(CLEARED_FILTERS)}
    >
      {t("projectsCommand.filters.clearAll")}
    </Button>
  ) : null

  const countPill = (count: number) =>
    count ? (
      <span className="rounded-full bg-teal-50 px-1.5 text-xs font-semibold leading-5 text-teal-800 tabular-nums">
        {count}
      </span>
    ) : null

  return (
    <Flex vertical gap={12} className="pt-5">
      {compact ? (
        <Flex gap={8} wrap="wrap" align="center" justify="space-between">
          {scopeSwitch}
          <Button
            icon={<FilterOutlined />}
            onClick={() => setDrawerOpen(true)}
            className={CONTROL}
            aria-label={
              chips.length
                ? t("projectsCommand.filters.withCount", {
                    label: t("projectsCommand.filters.title"),
                    count: chips.length
                  })
                : t("projectsCommand.filters.title")
            }
          >
            <span className="inline-flex items-center gap-2">
              {t("projectsCommand.filters.title")}
              {countPill(chips.length)}
            </span>
          </Button>
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            title={t("projectsCommand.filters.title")}
            placement="bottom"
            height="85%"
            extra={clearAll}
          >
            <Flex vertical gap={12} className="[&_.ant-btn]:w-full">
              {filterButtons}
              {moreFilters}
            </Flex>
          </Drawer>
        </Flex>
      ) : (
        <Flex gap={8} wrap="wrap" align="center">
          {/* Spacing, not a rule, separates whose projects from how they are narrowed. */}
          <div className="me-4">{scopeSwitch}</div>
          {filterButtons}
          <Popover
            trigger="click"
            placement="bottomRight"
            content={moreFilters}
            arrow={false}
          >
            <Button
              icon={<FilterOutlined />}
              className={CONTROL}
              aria-haspopup="dialog"
              aria-label={
                moreActive
                  ? t("projectsCommand.filters.withCount", {
                      label: t("projectsCommand.filters.more"),
                      count: moreActive
                    })
                  : t("projectsCommand.filters.more")
              }
            >
              <span className="inline-flex items-center gap-2">
                {t("projectsCommand.filters.more")}
                {countPill(moreActive)}
              </span>
            </Button>
          </Popover>
          <span className="ms-auto">{clearAll}</span>
        </Flex>
      )}

      {chips.length ? (
        <ul
          aria-label={t("projectsCommand.filters.applied")}
          className="m-0 flex list-none flex-wrap gap-2 p-0"
        >
          {chips.map((chip) => (
            <li
              key={chip.key}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-sky-50 ps-2.5 pe-1 text-[13px] text-sky-900"
            >
              {chip.label}
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined className="text-[10px]" />}
                aria-label={t("projectsCommand.filters.remove", {
                  label: chip.label
                })}
                onClick={() => onFiltersChange(chip.remove)}
                className="!h-5 !w-5 !min-w-0 text-sky-900"
              />
            </li>
          ))}
        </ul>
      ) : null}
    </Flex>
  )
}
